import React from 'react';
import { pascalCase } from 'change-case';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Banner from '@atlaskit/banner';
import Link from '@atlaskit/link';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import WarningIcon from '@atlaskit/icon/glyph/warning';

import type { ConfluenceEdition } from '@confluence/session-data';

const messages = defineMessages({
	trialEndsWarning: {
		id: 'experiment-eot-banner.trial-ends-warning',
		defaultMessage:
			'Your {edition} trial ends in {daysLeftInTrial, plural, one {# day} other {# days}}.',
		description: 'Inform the user that their trial is ending soon',
	},
	linkToPaymentDetails: {
		id: 'experiment-eot-banner.link-to-payment-details',
		defaultMessage:
			'<link>Add payment</link> and keep advanced features like unlimited users and page permissions.',
		description: 'CTA link to add payment details, with some benefits described',
	},
});

interface EotBannerProps {
	edition: ConfluenceEdition;
	paymentFlowUrl: string;
	daysLeftInTrial: number;
}

/**
 * The UI for the end-of-trial banner.
 */
export const EotBanner = ({ edition, paymentFlowUrl, daysLeftInTrial }: EotBannerProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClickPay = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'EotBanner',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'addPayment',
				attributes: { edition },
			},
		}).fire();
	};

	return (
		<Banner
			appearance={daysLeftInTrial > 2 ? 'warning' : 'error'}
			icon={
				daysLeftInTrial > 2 ? (
					<WarningIcon label="Warning" secondaryColor="inherit" />
				) : (
					<ErrorIcon label="Error" secondaryColor="inherit" />
				)
			}
		>
			<FormattedMessage
				{...messages.trialEndsWarning}
				values={{
					edition: pascalCase(edition),
					daysLeftInTrial,
				}}
			/>
			&nbsp;
			<FormattedMessage
				{...messages.linkToPaymentDetails}
				values={{
					link: (chunks) => (
						<Link onClick={onClickPay} target="_blank" href={paymentFlowUrl}>
							{chunks}
						</Link>
					),
				}}
			/>
		</Banner>
	);
};
